import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../../components/ui/Button";
import api from "../../../api/axiosInstance";

export default function Results() {
  const navigate = useNavigate();
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get user email from localStorage
        const userStr = localStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : null;
        
        if (!user || !user.email) {
          setError('User not found');
          setLoading(false);
          return;
        }

        // Fetch actual test results from database
        const response = await api.get(`/api/mock-test/results/${user.email}`);
        const resultData = response.data;

        // Calculate section-wise percentages and ensure proper section names
        const sectionScores = {};
        const expectedSections = {
          aptitude: { correct: 0, total: 10 },
          technical: { correct: 0, total: 10 },
          coding: { correct: 0, total: 5 }
        };

        // Initialize with expected sections
        Object.keys(expectedSections).forEach(section => {
          sectionScores[section] = {
            correct: 0,
            total: expectedSections[section].total,
            percentage: 0
          };
        });

        // Update with actual data from backend
        Object.entries(resultData.sectionScores).forEach(([topic, scores]) => {
          if (sectionScores[topic]) {
            sectionScores[topic].correct = scores.correct;
            sectionScores[topic].percentage = sectionScores[topic].total > 0 
              ? Math.round((scores.correct / sectionScores[topic].total) * 100) 
              : 0;
          }
        });

        // Calculate time taken
        const timeTaken = resultData.startedAt && resultData.endedAt 
          ? Math.round((new Date(resultData.endedAt) - new Date(resultData.startedAt)) / 60000)
          : 0;

        const formattedScoreData = {
          totalQuestions: 25, // Fixed total: 10 + 10 + 5 = 25
          correctAnswers: resultData.correctAnswers,
          percentage: resultData.correctAnswers > 0 
            ? Math.round((resultData.finalScore / 10) / 25 * 100) // Calculate based on actual points
            : 0,
          sectionScores: sectionScores,
          timeTaken: `${timeTaken} minutes`,
          finalScore: resultData.finalScore,
          finalFeedback: resultData.finalFeedback
        };

        setScore(formattedScoreData);
      } catch (err) {
        console.error('Error fetching results:', err);
        setError('Failed to load results. Please try again.');
        
        // Fallback to mock data if API fails
        const mockScoreData = {
          totalQuestions: 25,
          correctAnswers: 18,
          percentage: 72,
          sectionScores: {
            aptitude: { correct: 8, total: 10, percentage: 80 },
            technical: { correct: 7, total: 10, percentage: 70 },
            coding: { correct: 3, total: 5, percentage: 60 }
          },
          timeTaken: "25 minutes 30 seconds"
        };
        setScore(mockScoreData);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, []);

  const handleRetakeTest = () => {
    navigate("/candidate/mock-test/instructions");
  };

  const handleBackToDashboard = () => {
    navigate("/candidate/dashboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Results</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button
            variant="default"
            size="lg"
            onClick={handleBackToDashboard}
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Mock Test Results
            </h1>
            <p className="text-gray-600">
              Completed on {new Date().toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              })}
            </p>
          </div>

          {/* Overall Score */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 mb-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-600 mb-2">
                {score.percentage}%
              </div>
              <p className="text-gray-600 text-lg">
                {score.correctAnswers} out of {score.totalQuestions} questions correct
              </p>
              <p className="text-gray-500">
                Time taken: {score.timeTaken}
              </p>
            </div>
          </div>

          {/* Section Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {Object.entries(score.sectionScores).map(([section, data]) => (
              <div key={section} className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2 capitalize">
                  {section} Section
                </h3>
                <div className="space-y-1">
                  <p className="text-sm text-gray-600">
                    Score: {data.correct}/{data.total}
                  </p>
                  <p className="text-sm text-gray-600">
                    Percentage: {data.percentage}%
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${data.percentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="outline"
              size="lg"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={handleRetakeTest}
              className="w-full sm:w-auto"
            >
              Retake Test
            </Button>
            <Button
              variant="default"
              size="lg"
              iconName="Home"
              iconPosition="left"
              onClick={handleBackToDashboard}
              className="w-full sm:w-auto"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
